import { useCallback, useRef } from 'react';
import type { FeedEntry } from '~/services/layout/index.ts';
import {
  getAudioImageSrc,
  hasVisibleText,
  resolveEntryText,
} from './audio-player-utils.ts';
import { DragHandle } from '~/services/plugins/popup-menu/components/dnd/DragHandle';
import { DragPreviewLayer } from '~/services/plugins/popup-menu/components/dnd/DragPreviewLayer';
import { InsertionLine } from '~/services/plugins/popup-menu/components/dnd/InsertionLine';
import { KeyboardDndLiveRegion } from '~/services/plugins/popup-menu/components/dnd/KeyboardDndLiveRegion';
import type { SortableItemRect } from '~/services/plugins/popup-menu/components/dnd/types';
import { useSortableListDnd } from '~/services/plugins/popup-menu/components/dnd/use-sortable-list-dnd';
import { IconButton } from './icon-button';

type QueuePanelLabels = {
  queueTitle: string;
  nowPlayingLabel: string;
  nextUpTitle: string;
  nextUpEmptyLabel: string;
  clearQueueButtonLabel: string;
};

type QueuePanelAssets = {
  mobileNowPlayingPlayAsset?: string;
  mobileNowPlayingPauseAsset?: string;
  desktopNowPlayingPlayAsset?: string;
  desktopNowPlayingPauseAsset?: string;
  mobileQueueRemoveAsset?: string;
  desktopQueueRemoveAsset?: string;
  mobileQueueReorderAsset?: string;
  desktopQueueReorderAsset?: string;
};

type QueuePanelItemConfig = {
  mobileImageDataKey?: string;
  desktopImageDataKey?: string;
  subtitleDataKey?: string;
};

type QueuePanelNowPlayingConfig = {
  imageSrc?: string;
  mobileImageDataKey?: string;
  desktopImageDataKey?: string;
  mobileImagePlaceholderColor?: string;
  desktopImagePlaceholderColor?: string;
  mobileLabelEnabled: boolean;
  desktopLabelEnabled: boolean;
  mobileSubtitleEnabled: boolean;
  desktopSubtitleEnabled: boolean;
  mobileEqualizerAsset?: string;
  desktopEqualizerAsset?: string;
};

type QueuePanelTextTransforms = {
  mobilePanelTitle?: string;
  desktopPanelTitle?: string;
  mobileHeaderTitle?: string;
  mobileHeaderClearButton?: string;
  desktopHeaderTitle?: string;
  desktopHeaderClearButton?: string;
};

type QueuePanelProps = {
  isOpen: boolean;
  labels: QueuePanelLabels;
  assets: QueuePanelAssets;
  textTransforms: QueuePanelTextTransforms;
  itemConfig: QueuePanelItemConfig;
  nowPlayingConfig: QueuePanelNowPlayingConfig;
  isPlaying: boolean;
  currentTitle: string;
  currentSubtitle: string;
  queueItems: FeedEntry[];
  closeAsset: string;
  onClose: () => void;
  onClearQueue: () => void;
  onRemoveQueueItem: (payload: {
    itemId: string;
    queueInstanceId?: string;
  }) => void;
  onPlayQueueItem: (item: FeedEntry) => void;
  onReorderQueueItems: (payload: {
    fromIndex: number;
    toIndex: number;
  }) => void;
  onTogglePlayback?: () => void;
};

function getTextTransformClass(value?: string): string {
  if (value === 'uppercase') return 'uppercase';
  if (value === 'lowercase') return 'lowercase';
  if (value === 'capitalize') return 'capitalize';
  return '';
}

function QueueSectionHeader({
  isDesktop,
  title,
  clearButtonLabel,
  titleTextTransform,
  clearButtonTextTransform,
  onClear,
}: {
  isDesktop: boolean;
  title: string;
  clearButtonLabel?: string;
  titleTextTransform?: string;
  clearButtonTextTransform?: string;
  onClear?: () => void;
}): JSX.Element {
  const headerBorderClass = isDesktop
    ? 'border-queue-desktop-header'
    : 'border-queue-mobile-header';

  const titleClass = isDesktop
    ? 'text-queue-desktop-header-title font-queue-desktop-header-title text-queue-desktop-header-title-size leading-queue-desktop-header-title tracking-queue-desktop-header-title'
    : 'text-queue-mobile-header-title font-queue-mobile-header-title text-queue-mobile-header-title-size leading-queue-mobile-header-title tracking-queue-mobile-header-title';

  const clearButtonClass = isDesktop
    ? 'bg-queue-desktop-clear-button hover:bg-queue-desktop-clear-button-hover active:bg-queue-desktop-clear-button-hover focus-visible:bg-queue-desktop-clear-button-hover text-queue-desktop-clear-button font-queue-desktop-clear-button text-queue-desktop-clear-button-size leading-queue-desktop-clear-button tracking-queue-desktop-clear-button pt-queue-desktop-clear-button-t pr-queue-desktop-clear-button-r pb-queue-desktop-clear-button-b pl-queue-desktop-clear-button-l rounded-queue-desktop-clear-button'
    : 'bg-queue-mobile-clear-button hover:bg-queue-mobile-clear-button-hover active:bg-queue-mobile-clear-button-hover focus-visible:bg-queue-mobile-clear-button-hover text-queue-mobile-clear-button font-queue-mobile-clear-button text-queue-mobile-clear-button-size leading-queue-mobile-clear-button tracking-queue-mobile-clear-button pt-queue-mobile-clear-button-t pr-queue-mobile-clear-button-r pb-queue-mobile-clear-button-b pl-queue-mobile-clear-button-l rounded-queue-mobile-clear-button';

  return (
    <div
      className={`flex items-center justify-between border-b pb-2 ${headerBorderClass}`}
    >
      <p
        className={`${titleClass} ${getTextTransformClass(titleTextTransform)}`}
      >
        {title}
      </p>

      {clearButtonLabel && onClear ? (
        <button
          type="button"
          onClick={onClear}
          data-testid={
            isDesktop
              ? 'queue-clear-button-desktop'
              : 'queue-clear-button-mobile'
          }
          className={`${clearButtonClass} ${getTextTransformClass(
            clearButtonTextTransform
          )}`}
        >
          {clearButtonLabel}
        </button>
      ) : null}
    </div>
  );
}

function QueueItemsList({
  isDesktop,
  queueItems,
  assets,
  itemConfig,
  emptyLabel,
  onRemove,
  onPlay,
  onReorder,
  allowItemActions = true,
}: {
  isDesktop: boolean;
  queueItems: FeedEntry[];
  assets: QueuePanelAssets;
  itemConfig: QueuePanelItemConfig;
  emptyLabel: string;
  onRemove?: (payload: { itemId: string; queueInstanceId?: string }) => void;
  onPlay?: (item: FeedEntry) => void;
  onReorder?: (payload: { fromIndex: number; toIndex: number }) => void;
  allowItemActions?: boolean;
}): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const canReorder = queueItems.length > 1;

  const itemClass = isDesktop
    ? 'bg-queue-desktop-item'
    : 'bg-queue-mobile-item';

  const rowPaddingClass = isDesktop ? 'relative py-2' : 'relative py-[8px]';
  const itemHoverLayerClass = isDesktop
    ? 'pointer-events-none absolute inset-y-0 -left-2 -right-2 rounded-queue-desktop-item group-hover:bg-queue-desktop-item-hover group-focus-within:bg-queue-desktop-item-hover group-active:bg-queue-desktop-item-pressed'
    : 'pointer-events-none absolute inset-y-0 -left-2 -right-2 rounded-queue-mobile-item group-hover:bg-queue-mobile-item-hover group-focus-within:bg-queue-mobile-item-hover group-active:bg-queue-mobile-item-pressed';
  const actionGroupClass = 'flex items-center';
  const actionButtonHoverClass =
    'hover:bg-[#EFEFEF1A] focus-visible:bg-[#EFEFEF1A] active:bg-[#EFEFEF26]';
  const actionButtonClass = `h-10 w-10 rounded-full ${actionButtonHoverClass}`;
  const actionIconClass = 'h-5 w-5 shrink-0';

  const titleClass = isDesktop
    ? 'text-queue-desktop-item-title font-queue-desktop-item-title text-queue-desktop-item-title-size leading-queue-desktop-item-title tracking-queue-desktop-item-title group-focus-within:text-queue-desktop-item-title-focused'
    : 'text-queue-mobile-item-title font-queue-mobile-item-title text-queue-mobile-item-title-size leading-queue-mobile-item-title tracking-queue-mobile-item-title group-focus-within:text-queue-mobile-item-title-focused';

  const subtitleClass = isDesktop
    ? 'text-queue-desktop-item-subtitle font-queue-desktop-item-subtitle text-queue-desktop-item-subtitle-size leading-queue-desktop-item-subtitle tracking-queue-desktop-item-subtitle'
    : 'text-queue-mobile-item-subtitle font-queue-mobile-item-subtitle text-queue-mobile-item-subtitle-size leading-queue-mobile-item-subtitle tracking-queue-mobile-item-subtitle';

  const removeAsset = isDesktop
    ? assets.desktopQueueRemoveAsset
    : assets.mobileQueueRemoveAsset;

  const reorderAsset = isDesktop
    ? assets.desktopQueueReorderAsset
    : assets.mobileQueueReorderAsset;

  const imageDataKey = isDesktop
    ? itemConfig.desktopImageDataKey
    : itemConfig.mobileImageDataKey;

  const getItemKey = useCallback((item: FeedEntry, index: number): string => {
    const queueItemId =
      item.extensions?.queueItemId || item.extensions?.queue_instance_id;
    if (queueItemId) return String(queueItemId);

    return `${item.id}::${index}`;
  }, []);

  const getItemRects = useCallback((): SortableItemRect[] => {
    return queueItems
      .map((item, index) => {
        const itemKey = getItemKey(item, index);
        const element = rowRefs.current[itemKey];

        if (!element) return null;

        const rect = element.getBoundingClientRect();

        return {
          id: itemKey,
          index,
          top: rect.top,
          height: rect.height,
        };
      })
      .filter((rect): rect is SortableItemRect => Boolean(rect));
  }, [getItemKey, queueItems]);
  const {
    isDragging,
    dragItemKey,
    dragSourceIndex,
    dragPreview,
    activeDropTarget,
    liveMessage,
    isKeyboardDragging,
    handlePressStart,
    consumeSuppressedRowClick,
    isRowActionBlocked,
  } = useSortableListDnd({
    listLength: queueItems.length,
    canReorder,
    getItemRects,
    getItemRectById: (itemId: string): DOMRect | null => {
      const element = rowRefs.current[itemId];
      if (!element) return null;

      return element.getBoundingClientRect();
    },
    getListElement: (): HTMLDivElement | null => listRef.current,
    onCommitReorder: ({ fromIndex, toIndex }): void => {
      onReorder?.({ fromIndex, toIndex });
    },
  });

  if (!queueItems.length) {
    return (
      <p
        className={`py-3 text-center ${subtitleClass}`}
        data-testid="queue-next-up-empty"
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div ref={listRef}>
      <KeyboardDndLiveRegion message={liveMessage} />
      <div>
        {queueItems.map((item, index) => {
          const itemKey = getItemKey(item, index);
          const isDraggedItem = dragItemKey === itemKey;
          const showBeforeLine =
            activeDropTarget?.index === index &&
            activeDropTarget.side === 'before';
          const showAfterLine =
            activeDropTarget?.index === index &&
            activeDropTarget.side === 'after';
          const subtitle = resolveEntryText(item, itemConfig.subtitleDataKey);

          return (
            <div key={itemKey} className="space-y-1">
              <InsertionLine visible={Boolean(showBeforeLine)} side="before" />

              <div
                ref={(element) => {
                  rowRefs.current[itemKey] = element;
                }}
                data-testid={`queue-item-${item.id}`}
                className={`group rounded transition-all duration-150 ${itemClass} ${rowPaddingClass} ${
                  isDraggedItem && isDragging ? 'opacity-40' : 'opacity-100'
                }`}
                onClick={() => {
                  if (consumeSuppressedRowClick()) {
                    return;
                  }

                  if (isRowActionBlocked()) {
                    return;
                  }

                  onPlay?.(item);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  if (isKeyboardDragging) return;

                  event.preventDefault();
                  onPlay?.(item);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Play ${item.title || item.id}`}
              >
                <div aria-hidden className={itemHoverLayerClass} />
                <div className="relative flex items-center gap-3">
                  <div
                    className="shrink-0 overflow-hidden"
                    style={{
                      width: isDesktop
                        ? 'var(--desktop_queue_item_image_width, 48px)'
                        : 'var(--mobile_queue_item_image_width, 48px)',
                      height: isDesktop
                        ? 'var(--desktop_queue_item_image_height, 48px)'
                        : 'var(--mobile_queue_item_image_height, 48px)',
                      borderWidth: isDesktop
                        ? 'var(--desktop_queue_item_image_border_width, 0px)'
                        : 'var(--mobile_queue_item_image_border_width, 0px)',
                      borderColor: isDesktop
                        ? 'var(--desktop_queue_item_image_border_color, transparent)'
                        : 'var(--mobile_queue_item_image_border_color, transparent)',
                      borderRadius: isDesktop
                        ? 'var(--desktop_queue_item_image_corner_radius, 8px)'
                        : 'var(--mobile_queue_item_image_corner_radius, 8px)',
                      backgroundColor: isDesktop
                        ? 'var(--desktop_queue_item_image_placeholder_color, transparent)'
                        : 'var(--mobile_queue_item_image_placeholder_color, transparent)',
                    }}
                  >
                    {(() => {
                      const itemImageSrc =
                        getAudioImageSrc(item, imageDataKey) ||
                        (item.extensions?.imageSrc as string | undefined);

                      return itemImageSrc ? (
                        <img
                          src={itemImageSrc}
                          alt={
                            item.title ||
                            item.id ||
                            imageDataKey ||
                            'Queue item image'
                          }
                          className="h-full w-full object-cover"
                        />
                      ) : null;
                    })()}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className={`truncate ${titleClass}`}>
                      {item.title || item.id}
                    </p>
                    {hasVisibleText(subtitle) ? (
                      <p className={`truncate ${subtitleClass}`}>{subtitle}</p>
                    ) : null}
                  </div>

                  <div className={actionGroupClass}>
                    {allowItemActions && removeAsset && onRemove ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemove({
                            itemId: item.id,
                            queueInstanceId:
                              (item.extensions?.queueItemId as
                                | string
                                | undefined) ||
                              (item.extensions?.queue_instance_id as
                                | string
                                | undefined),
                          });
                        }}
                        data-testid={`queue-remove-button-${item.id}`}
                        className={`flex ${actionButtonClass} items-center justify-center focus-visible:outline-none`}
                        aria-label={`Remove ${
                          item.title || item.id
                        } from queue`}
                      >
                        <img
                          src={removeAsset}
                          alt="Remove from queue"
                          className={actionIconClass}
                        />
                      </button>
                    ) : null}

                    {allowItemActions ? (
                      <DragHandle
                        ariaLabel={`Reorder ${item.title || item.id}`}
                        className={`!h-10 !w-10 !rounded-full ${actionButtonHoverClass}`}
                        disabled={!canReorder}
                        onPressStart={({ input, clientX, clientY }) => {
                          handlePressStart({
                            input,
                            clientX,
                            clientY,
                            index,
                            itemKey,
                            itemLabel: item.title || item.id,
                          });
                        }}
                      >
                        {reorderAsset ? (
                          <img
                            src={reorderAsset}
                            alt="Reorder handle"
                            className={actionIconClass}
                          />
                        ) : undefined}
                      </DragHandle>
                    ) : null}
                  </div>
                </div>
              </div>

              <InsertionLine visible={Boolean(showAfterLine)} side="after" />
            </div>
          );
        })}
      </div>

      <DragPreviewLayer
        visible={Boolean(isDragging && dragPreview)}
        x={dragPreview?.x || 0}
        y={dragPreview?.y || 0}
        offsetX={dragPreview?.offsetX || 0}
        offsetY={dragPreview?.offsetY || 0}
      >
        <div className="rounded border border-white/20 bg-[#1a1a1a] px-3 py-2 text-sm text-white shadow-lg">
          {dragSourceIndex !== null
            ? queueItems[dragSourceIndex]?.title ||
              queueItems[dragSourceIndex]?.id
            : ''}
        </div>
      </DragPreviewLayer>
    </div>
  );
}

function QueuePanelSurface({
  isDesktop,
  labels,
  assets,
  textTransforms,
  itemConfig,
  nowPlayingConfig,
  isPlaying,
  currentTitle,
  currentSubtitle,
  queueItems,
  closeAsset,
  onClose,
  onClearQueue,
  onRemoveQueueItem,
  onPlayQueueItem,
  onReorderQueueItems,
  onTogglePlayback,
}: {
  isDesktop: boolean;
  labels: QueuePanelLabels;
  assets: QueuePanelAssets;
  textTransforms: QueuePanelTextTransforms;
  itemConfig: QueuePanelItemConfig;
  nowPlayingConfig: QueuePanelNowPlayingConfig;
  isPlaying: boolean;
  currentTitle: string;
  currentSubtitle: string;
  queueItems: FeedEntry[];
  closeAsset: string;
  onClose: () => void;
  onClearQueue: () => void;
  onRemoveQueueItem: (payload: {
    itemId: string;
    queueInstanceId?: string;
  }) => void;
  onPlayQueueItem: (item: FeedEntry) => void;
  onReorderQueueItems: (payload: {
    fromIndex: number;
    toIndex: number;
  }) => void;
  onTogglePlayback?: () => void;
}): JSX.Element {
  const nowPlayingAsset = isDesktop
    ? isPlaying
      ? assets.desktopNowPlayingPauseAsset || assets.desktopNowPlayingPlayAsset
      : assets.desktopNowPlayingPlayAsset || assets.desktopNowPlayingPauseAsset
    : isPlaying
    ? assets.mobileNowPlayingPauseAsset || assets.mobileNowPlayingPlayAsset
    : assets.mobileNowPlayingPlayAsset || assets.mobileNowPlayingPauseAsset;

  const nowPlayingContainerClass = isDesktop
    ? 'bg-queue-desktop-now-playing rounded-queue-desktop-now-playing'
    : 'bg-queue-mobile-now-playing rounded-queue-mobile-now-playing';

  const nowPlayingLabelEnabled = isDesktop
    ? nowPlayingConfig.desktopLabelEnabled
    : nowPlayingConfig.mobileLabelEnabled;

  const nowPlayingSubtitleEnabled = isDesktop
    ? nowPlayingConfig.desktopSubtitleEnabled
    : nowPlayingConfig.mobileSubtitleEnabled;

  const nowPlayingEqualizerAsset = isDesktop
    ? nowPlayingConfig.desktopEqualizerAsset
    : nowPlayingConfig.mobileEqualizerAsset;

  const nowPlayingImageDataKey = isDesktop
    ? nowPlayingConfig.desktopImageDataKey
    : nowPlayingConfig.mobileImageDataKey;

  const nowPlayingImagePlaceholderColor = isDesktop
    ? nowPlayingConfig.desktopImagePlaceholderColor
    : nowPlayingConfig.mobileImagePlaceholderColor;

  const nowPlayingLabelClass = isDesktop
    ? 'text-queue-desktop-now-playing-label font-queue-desktop-now-playing-label text-queue-desktop-now-playing-label-size leading-queue-desktop-now-playing-label tracking-queue-desktop-now-playing-label'
    : 'text-queue-mobile-now-playing-label font-queue-mobile-now-playing-label text-queue-mobile-now-playing-label-size leading-queue-mobile-now-playing-label tracking-queue-mobile-now-playing-label';

  const nowPlayingTitleClass = isDesktop
    ? 'text-queue-desktop-now-playing-title font-queue-desktop-now-playing-title text-queue-desktop-now-playing-title-size leading-queue-desktop-now-playing-title tracking-queue-desktop-now-playing-title'
    : 'text-queue-mobile-now-playing-title font-queue-mobile-now-playing-title text-queue-mobile-now-playing-title-size leading-queue-mobile-now-playing-title tracking-queue-mobile-now-playing-title';

  const nowPlayingSubtitleClass = isDesktop
    ? 'text-queue-desktop-now-playing-subtitle font-queue-desktop-now-playing-subtitle text-queue-desktop-now-playing-subtitle-size leading-queue-desktop-now-playing-subtitle tracking-queue-desktop-now-playing-subtitle'
    : 'text-queue-mobile-now-playing-subtitle font-queue-mobile-now-playing-subtitle text-queue-mobile-now-playing-subtitle-size leading-queue-mobile-now-playing-subtitle tracking-queue-mobile-now-playing-subtitle';

  const panelTitleTransform = isDesktop
    ? textTransforms.desktopPanelTitle
    : textTransforms.mobilePanelTitle;

  const headerTitleTransform = isDesktop
    ? textTransforms.desktopHeaderTitle
    : textTransforms.mobileHeaderTitle;

  const clearButtonTransform = isDesktop
    ? textTransforms.desktopHeaderClearButton
    : textTransforms.mobileHeaderClearButton;

  const panelSurfaceClass = isDesktop
    ? 'h-full w-[350px] rounded-queue-desktop-panel border border-queue-desktop-panel bg-queue-desktop-panel'
    : 'h-full w-full bg-queue-mobile-panel';

  const panelHeaderTitleClass = isDesktop
    ? 'text-queue-desktop-panel-title font-queue-desktop-panel-title text-queue-desktop-panel-title-size leading-queue-desktop-panel-title tracking-queue-desktop-panel-title'
    : 'text-queue-mobile-panel-title font-queue-mobile-panel-title text-queue-mobile-panel-title-size leading-queue-mobile-panel-title tracking-queue-mobile-panel-title';

  return (
    <div
      className={`flex h-full flex-col overflow-hidden ${panelSurfaceClass}`}
      data-testid={
        isDesktop ? 'queue-panel-surface-desktop' : 'queue-panel-surface-mobile'
      }
    >
      <div
        className={`flex items-center justify-between ${
          isDesktop ? 'gap-3 px-4 pt-3' : 'px-[20px] pt-[10px]'
        }`}
      >
        <p
          className={`${panelHeaderTitleClass} ${getTextTransformClass(
            panelTitleTransform
          )}`}
          data-testid="queue-panel-title"
        >
          {labels.queueTitle}
        </p>
        <IconButton
          src={closeAsset}
          alt="Close queue panel"
          onClick={onClose}
          size={40}
          iconSize={20}
          iconColor="#efefef"
          buttonClassName="group flex items-center justify-center rounded-full hover:bg-[#EFEFEF1A] focus-visible:bg-[#EFEFEF1A] active:bg-[#EFEFEF26]"
          iconClassName="queue-close-icon"
        />
      </div>

      <div
        className={`flex-1 overflow-y-auto ${isDesktop ? 'px-4' : 'px-[20px]'}`}
      >
        <div
          data-testid={
            isDesktop
              ? 'queue-panel-content-desktop'
              : 'queue-panel-content-mobile'
          }
        >
          <div>
            <div
              className={`flex items-center gap-3 ${
                isDesktop ? 'pb-3 pt-2' : 'py-4'
              } ${nowPlayingContainerClass}`}
              data-testid="queue-now-playing"
            >
              {nowPlayingConfig.imageSrc ? (
                <div
                  className="shrink-0 overflow-hidden"
                  style={{
                    width: isDesktop
                      ? 'var(--desktop_now_playing_image_width, 48px)'
                      : 'var(--mobile_now_playing_image_width, 48px)',
                    height: isDesktop
                      ? 'var(--desktop_now_playing_image_height, 48px)'
                      : 'var(--mobile_now_playing_image_height, 48px)',
                    borderWidth: isDesktop
                      ? 'var(--desktop_now_playing_image_border_width, 0px)'
                      : 'var(--mobile_now_playing_image_border_width, 0px)',
                    borderColor: isDesktop
                      ? 'var(--desktop_now_playing_image_border_color, transparent)'
                      : 'var(--mobile_now_playing_image_border_color, transparent)',
                    borderRadius: isDesktop
                      ? 'var(--desktop_now_playing_image_corner_radius, 8px)'
                      : 'var(--mobile_now_playing_image_corner_radius, 8px)',
                    backgroundColor:
                      nowPlayingImagePlaceholderColor || 'transparent',
                  }}
                >
                  <img
                    src={nowPlayingConfig.imageSrc}
                    alt={
                      currentTitle ||
                      nowPlayingImageDataKey ||
                      'Now Playing item image'
                    }
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : null}

              <div className="min-w-0 flex-1">
                {nowPlayingLabelEnabled || nowPlayingEqualizerAsset ? (
                  <div className="flex items-center gap-2">
                    {nowPlayingEqualizerAsset ? (
                      <img
                        src={nowPlayingEqualizerAsset}
                        alt="Now playing equalizer"
                        className="h-[16px] w-[16px] shrink-0"
                      />
                    ) : null}
                    {nowPlayingLabelEnabled ? (
                      <p className={`truncate ${nowPlayingLabelClass}`}>
                        {labels.nowPlayingLabel}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {hasVisibleText(currentTitle) ? (
                  <p className={`truncate ${nowPlayingTitleClass}`}>
                    {currentTitle}
                  </p>
                ) : null}
                {nowPlayingSubtitleEnabled &&
                hasVisibleText(currentSubtitle) ? (
                  <p className={`truncate ${nowPlayingSubtitleClass}`}>
                    {currentSubtitle}
                  </p>
                ) : null}
              </div>

              {nowPlayingAsset ? (
                <button
                  type="button"
                  onClick={onTogglePlayback}
                  aria-label={isPlaying ? 'Pause playback' : 'Start playback'}
                  className="h-[40px] w-[40px] shrink-0 cursor-pointer transition-opacity hover:opacity-80"
                >
                  <img
                    src={nowPlayingAsset}
                    alt={isPlaying ? 'Pause asset' : 'Play asset'}
                    className="h-[40px] w-[40px] shrink-0"
                  />
                </button>
              ) : null}
            </div>
          </div>

          <div>
            <QueueSectionHeader
              isDesktop={isDesktop}
              title={labels.nextUpTitle}
              clearButtonLabel={labels.clearQueueButtonLabel}
              titleTextTransform={headerTitleTransform}
              clearButtonTextTransform={clearButtonTransform}
              onClear={onClearQueue}
            />
            <QueueItemsList
              isDesktop={isDesktop}
              queueItems={queueItems}
              assets={assets}
              itemConfig={itemConfig}
              emptyLabel={labels.nextUpEmptyLabel}
              onRemove={onRemoveQueueItem}
              onPlay={onPlayQueueItem}
              onReorder={onReorderQueueItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders the queue panel shell using Queue Web plugin localizations/styles.
 */
export function QueuePanel({
  isOpen,
  labels,
  assets,
  textTransforms,
  itemConfig,
  nowPlayingConfig,
  isPlaying,
  currentTitle,
  currentSubtitle,
  queueItems,
  closeAsset,
  onClose,
  onClearQueue,
  onRemoveQueueItem,
  onPlayQueueItem,
  onReorderQueueItems,
  onTogglePlayback,
}: QueuePanelProps): JSX.Element | null {
  if (!isOpen) return null;

  return (
    <div className="pointer-events-auto fixed inset-0 z-[10000] bg-black/40 desktop:z-[1500] desktop:bg-transparent">
      <div
        className="absolute inset-0 cursor-default"
        aria-hidden
        onClick={onClose}
      />

      <div
        className="absolute inset-0 desktop:bottom-[120px] desktop:left-auto desktop:right-3 desktop:top-[84px] desktop:w-[350px]"
        data-testid="queue-panel-positioner"
      >
        <div className="h-full desktop:hidden">
          <QueuePanelSurface
            isDesktop={false}
            labels={labels}
            assets={assets}
            textTransforms={textTransforms}
            itemConfig={itemConfig}
            nowPlayingConfig={nowPlayingConfig}
            isPlaying={isPlaying}
            currentTitle={currentTitle}
            currentSubtitle={currentSubtitle}
            queueItems={queueItems}
            closeAsset={closeAsset}
            onClose={onClose}
            onClearQueue={onClearQueue}
            onRemoveQueueItem={onRemoveQueueItem}
            onPlayQueueItem={onPlayQueueItem}
            onReorderQueueItems={onReorderQueueItems}
            onTogglePlayback={onTogglePlayback}
          />
        </div>

        <div className="hidden h-full desktop:block">
          <QueuePanelSurface
            isDesktop={true}
            labels={labels}
            assets={assets}
            textTransforms={textTransforms}
            itemConfig={itemConfig}
            nowPlayingConfig={nowPlayingConfig}
            isPlaying={isPlaying}
            currentTitle={currentTitle}
            currentSubtitle={currentSubtitle}
            queueItems={queueItems}
            closeAsset={closeAsset}
            onClose={onClose}
            onClearQueue={onClearQueue}
            onRemoveQueueItem={onRemoveQueueItem}
            onPlayQueueItem={onPlayQueueItem}
            onReorderQueueItems={onReorderQueueItems}
            onTogglePlayback={onTogglePlayback}
          />
        </div>
      </div>
    </div>
  );
}
